import { useEffect } from "react";

interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: "website" | "article";
  publishedTime?: string;
  modifiedTime?: string;
  tags?: string[];
  schema?: Record<string, unknown>;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  ogType = "website",
  publishedTime,
  modifiedTime,
  tags,
  schema,
}: SEOHeadProps) {
  const baseUrl = "https://91jingxuan.com";
  const fullTitle = `${title} - 91精选`;
  const resolvedCanonical = canonicalUrl ? `${baseUrl}${canonicalUrl}` : baseUrl;
  const resolvedOgImage = ogImage || `${baseUrl}/og-default.jpg`;

  useEffect(() => {
    document.title = fullTitle;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", description);
    setMeta("keywords", keywords);

    setMeta("og:title", fullTitle, true);
    setMeta("og:description", description, true);
    setMeta("og:type", ogType, true);
    setMeta("og:url", resolvedCanonical, true);
    setMeta("og:image", resolvedOgImage, true);
    setMeta("og:site_name", "91精选", true);
    setMeta("og:locale", "zh_CN", true);

    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", description);
    setMeta("twitter:image", resolvedOgImage);

    if (publishedTime) setMeta("article:published_time", publishedTime);
    if (modifiedTime) setMeta("article:modified_time", modifiedTime);
    if (tags) {
      tags.forEach((tag) => setMeta("article:tag", tag));
    }

    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", resolvedCanonical);
  }, [fullTitle, description, keywords, resolvedCanonical, ogType, resolvedOgImage, publishedTime, modifiedTime, tags]);

  useEffect(() => {
    if (!schema) return;
    const scriptId = "json-ld-schema";
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.setAttribute("type", "application/ld+json");
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema, null, 2);
  }, [schema]);

  return null;
}